import React from 'react';
import { certificationProviders } from '../mockData';
import { Award, Calendar, ExternalLink, CheckCircle, XCircle } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { Button } from './ui/button';

const CertificationsSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const getStatusColor = (status) => {
    return status === 'Active' ? 'text-green-400 bg-green-400/10' : 'text-red-400 bg-red-400/10';
  };

  const getStatusIcon = (status) => {
    return status === 'Active' ? <CheckCircle size={14} /> : <XCircle size={14} />;
  };

  return (
    <section
      id="certifications"
      className="min-h-screen flex items-center justify-center py-20 relative"
      style={{ backgroundColor: '#020817' }}
      ref={ref}
    >
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Header */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Certifications
          </h2>
          <p className="text-gray-400 text-lg">
            Verified credentials with direct links to the official certificates.
          </p>
        </motion.div>

        {/* Provider Cards */}
        <div className="space-y-8">
          {certificationProviders.map((provider, providerIndex) => (
            <motion.div
              key={provider.provider}
              className="rounded-2xl p-6 md:p-8"
              style={{
                backgroundColor: '#020f25',
                border: '1px solid rgba(148, 163, 184, 0.1)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              }}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: providerIndex * 0.15 }}
            >
              {/* Provider Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-700/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-400/20 flex items-center justify-center">
                    <Award className="text-cyan-400" size={20} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{provider.provider}</h3>
                    <p className="text-sm text-gray-400">
                      {provider.certifications.length} {provider.certifications.length === 1 ? 'certification' : 'certifications'}
                    </p>
                  </div>
                </div>
                <div className="w-10 h-10 rounded-full bg-cyan-400/10 flex items-center justify-center">
                  <span className="text-cyan-400 font-bold">{provider.certifications.length}</span>
                </div>
              </div>

              {/* Certifications List */}
              <div className="space-y-6">
                {provider.certifications.map((cert, certIndex) => (
                  <div
                    key={certIndex}
                    className="flex flex-col lg:flex-row gap-6 p-4 rounded-xl hover:bg-gray-800/30 transition-all duration-300"
                  >
                    {/* Credential Image */}
                    <div className="flex-shrink-0 lg:w-80">
                      <div
                        className="rounded-xl overflow-hidden"
                        style={{
                          backgroundColor: '#0f172a',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
                        }}
                      >
                        <img
                          src={cert.credentialImageUrl}
                          alt={cert.title}
                          width={340}
                          height={340}
                          loading="lazy"
                          className="w-full h-auto object-contain p-4"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>

                    {/* Certification Details */}
                    <div className="flex-1 space-y-4">
                      <div>
                        <h4 className="text-xl font-bold text-white mb-2 leading-tight">
                          {cert.title}
                        </h4>
                        <p className="text-gray-400 text-sm">{cert.issuer}</p>
                      </div>

                      {/* Info Pills */}
                      <div className="flex flex-wrap gap-2">
                        {/* Issued Date */}
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800/50 rounded-full text-sm text-gray-300">
                          <Calendar size={14} />
                          <span>Issued {cert.issuedOn}</span>
                        </div>

                        {/* Expiry Date */}
                        {cert.expiresOn && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800/50 rounded-full text-sm text-gray-300">
                            <Calendar size={14} />
                            <span>Valid until {cert.expiresOn}</span>
                          </div>
                        )}

                        {/* Status */}
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${getStatusColor(cert.status)}`}>
                          {getStatusIcon(cert.status)}
                          <span>{cert.status}</span>
                        </div>
                      </div>

                      {/* View Certificate Button */}
                      <div className="pt-2">
                        <Button
                          onClick={() => window.open(cert.verificationUrl, '_blank')}
                          className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300 hover:scale-105 flex items-center gap-2"
                        >
                          View Certificate
                          <ExternalLink size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CertificationsSection;
